import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-router-dom';
import {
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const ConsumptionOverallChart = ({
    consumptionData,
    isLoading,
    portfolio,
    selectedPortfolio,
    selectedUnit,
    handlePortfolioChange,
    handleUnitChange,
    accountAndEndpoints
}) => {
    const transformedData = transformData(consumptionData);

    const [accountName, setAccountName] = useState('');
    const [endpointForSelectedAccount, setEndpointForSelectedAccount] =
        useState('');
    const [portfolioData, setPortfolioData] = useState([]);

    const selectedAccount = accountAndEndpoints.find(
        (item) => item.account_name === accountName
    );
    const selectedAccountEndpoints = selectedAccount?.endpoint || [];

    useEffect(() => {
        if (accountAndEndpoints.length > 0) {
            const first = accountAndEndpoints[0];
            setAccountName(first.account_name);
            setEndpointForSelectedAccount(first.endpoint?.[0]?.uuid || '');
        }
    }, [accountAndEndpoints]);

    useEffect(() => {
        const firstEndpointUuid = selectedAccountEndpoints?.[0]?.uuid || '';
        setEndpointForSelectedAccount(firstEndpointUuid);
    }, [accountName]); // reset endpoint when account changes

    // Filter portfolio data based on selected account and endpoint
    useEffect(() => {
        if (portfolio.length > 0) {
            const filtered = portfolio.filter((item) => {
                const matchesAccount = item?.account_name === accountName;
                const matchesEndpoint =
                    item?.endpoint?.uuid === endpointForSelectedAccount;
                return matchesAccount && matchesEndpoint;
            });
            setPortfolioData(filtered);
        }
    }, [accountName, endpointForSelectedAccount, portfolio]);

    useEffect(() => {
        if (portfolioData.length === 0) {
            return;
        }

        const hasSelectedPortfolio = portfolioData.some(
            (item) => item.uuid === selectedPortfolio?.uuid
        );

        if (!hasSelectedPortfolio) {
            handlePortfolioChange({
                target: { value: portfolioData[0].uuid }
            });
        }
    }, [portfolioData, selectedPortfolio?.uuid]);

    return (
        <>
            <Box marginBottom={`14px`} display={'flex'}>
                <FormControl
                    size="small"
                    sx={{
                        minWidth: 'min-content',
                        marginRight: '28px'
                    }}
                >
                    <InputLabel
                        id="demo-simple-select-label"
                        sx={{
                            fontSize: '12px',
                            marginTop: '5px'
                        }}
                    >
                        Accounts
                    </InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Accounts"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        // defaultValue={accountName}
                        sx={{
                            fontSize: '12px',
                            minWidth: '96px',
                            maxWidth: 'min-content'
                        }}
                    >
                        {accountAndEndpoints.map((item, index) => (
                            <MenuItem
                                key={index}
                                value={item.account_name}
                                sx={{
                                    fontSize: '12px'
                                }}
                            >
                                {item.account_name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl
                    size="small"
                    sx={{
                        minWidth: 'min-content',
                        marginRight: '28px'
                    }}
                >
                    <InputLabel
                        id="demo-simple-select-label"
                        sx={{
                            fontSize: '12px',
                            marginTop: '5px'
                        }}
                    >
                        Endpoint
                    </InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Endpoint"
                        value={endpointForSelectedAccount}
                        onChange={(e) =>
                            setEndpointForSelectedAccount(e.target.value)
                        }
                        // defaultValue={endpointForSelectedAccount}
                        sx={{
                            fontSize: '12px',
                            minWidth: '96px',
                            maxWidth: 'min-content'
                        }}
                    >
                        {selectedAccountEndpoints.map((ep, index) => (
                            <MenuItem
                                key={index}
                                value={ep.uuid}
                                sx={{
                                    fontSize: '12px'
                                }}
                            >
                                {ep.name} - {ep.location}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl
                    size="small"
                    sx={{
                        minWidth: 'min-content',
                        marginRight: '28px'
                    }}
                >
                    <InputLabel
                        id="demo-simple-select-label"
                        sx={{
                            fontSize: '12px',
                            marginTop: '5px'
                        }}
                    >
                        Portfolio
                    </InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Portfolio"
                        onChange={handlePortfolioChange}
                        value={selectedPortfolio?.uuid || ''}
                        // defaultValue={selectedPortfolio?.uuid || ''}
                        sx={{
                            fontSize: '12px',
                            minWidth: '96px',
                            maxWidth: 'min-content'
                        }}
                    >
                        {portfolioData.map((item, index) => (
                            <MenuItem
                                key={index}
                                value={item.uuid}
                                sx={{
                                    fontSize: '12px'
                                }}
                            >
                                {item.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small">
                    <InputLabel
                        id="demo-simple-select-label"
                        sx={{
                            fontSize: '12px',
                            marginTop: '5px'
                        }}
                    >
                        Unit
                    </InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Unit"
                        onChange={handleUnitChange}
                        value={selectedUnit}
                        // defaultValue={selectedUnit}
                        sx={{
                            fontSize: '12px',
                            minWidth: '96px',
                            maxWidth: 'min-content'
                        }}
                    >
                        <MenuItem
                            value={'mw'}
                            sx={{
                                fontSize: '12px'
                            }}
                        >
                            MW
                        </MenuItem>
                        <MenuItem
                            value={'mwh'}
                            sx={{
                                fontSize: '12px'
                            }}
                        >
                            MWh
                        </MenuItem>
                    </Select>
                </FormControl>
            </Box>
            {isLoading ? (
                <p className="h-[90%] flex items-center justify-center">
                    Loading....
                </p>
            ) : (
                <ResponsiveContainer width="100%" height="95%">
                    <ComposedChart
                        width={500}
                        height={400}
                        data={transformedData}
                    >
                        <CartesianGrid stroke="#f5f5f5" />
                        <XAxis
                            dataKey="name"
                            type="category"
                            interval={5}
                            fontSize={12}
                        />
                        <YAxis
                            fontSize={12}
                            tickCount={10}
                            interval="preserveEnd"
                        />
                        <Tooltip />
                        <Legend />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Year"
                            fill="#8BC1F7"
                            order={1}
                        />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Month"
                            fill="#7CC674"
                            order={2}
                        />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Quarter"
                            fill="#73C5C5"
                            order={3}
                        />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Others"
                            fill="#8481DD"
                            order={4}
                        />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="GY"
                            fill="#F4C145"
                            order={5}
                        />{' '}
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Summer"
                            fill="#EC7A08"
                            order={6}
                        />
                        <Bar
                            stackId={'a'}
                            type="monotone"
                            dataKey="Winter"
                            fill="#B8BBBE"
                        />
                        <Line
                            dataKey="Demand"
                            stroke="#05c46b"
                            strokeWidth={3}
                            connectNulls
                        />
                        <Line
                            dataKey="Demand 2"
                            stroke="#ffa801"
                            strokeWidth={3}
                            connectNulls
                        />
                        <Line
                            dataKey="Demand 3"
                            stroke="#3c40c6"
                            strokeWidth={3}
                            connectNulls
                        />
                        <Line
                            dataKey="Demand 4"
                            stroke="#0652DD"
                            strokeWidth={3}
                            connectNulls
                        />
                        <Line
                            dataKey="Demand 5"
                            stroke="#B53471"
                            strokeWidth={3}
                            connectNulls
                        />
                        <Line
                            dataKey="MEV"
                            stroke="#ff7300"
                            strokeWidth={3}
                            connectNulls
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            )}
        </>
    );
};

export default ConsumptionOverallChart;

function transformData(datasets) {
    const transformedData = [];
    const datasetNames = new Set();

    // Get all the unique x-values across all datasets
    const uniqueXValues = new Set();
    let timeDataset = null;
    datasets.forEach((dataset) => {
        if (dataset.name === 'Time') {
            timeDataset = dataset;
        } else {
            dataset.data.forEach(([x]) => {
                uniqueXValues.add(x);
            });
            datasetNames.add(dataset.name);
        }
    });

    // If a 'Time' dataset is provided, use its data for x-axis values
    const sortedXValues = timeDataset
        ? timeDataset.data
        : [...uniqueXValues].sort((a, b) => a - b);

    sortedXValues.forEach((xValue) => {
        const dataPoint = { name: xValue.toString() };

        // Initialize all dataset values to undefined
        Array.from(datasetNames).forEach((datasetName) => {
            dataPoint[datasetName] = undefined;
        });

        // Populate the dataset values for this x-value
        datasets.forEach((dataset) => {
            if (dataset.name === 'Time') return; // Skip the 'Time' dataset

            const { name: datasetName, data } = dataset;
            const yValue = data.find(([x]) => {
                return (
                    x - 1 ===
                    timeDataset.data.findIndex((x) => {
                        return x === xValue;
                    })
                );
            })?.[1];
            dataPoint[datasetName] = yValue;
        });

        transformedData.push(dataPoint);
    });

    return transformedData;
}
